import { API_CONFIG } from '@/config/api';
import { buildHandoffEntryUrl } from './reauth';

export type RedirectReason = 'unauthenticated' | 'session-expired' | 'logged-out';

/**
 * sessionStorage key marking that this tab has already spent its one
 * deep-linked redirect attempt without a session coming back.
 *
 * sessionStorage (not localStorage) is deliberate: per-tab and cleared on tab
 * close is exactly the lifetime a loop guard needs — a fresh tab, or this tab
 * after a real sign-in, should get the deep link again rather than inheriting
 * another tab's bad luck.
 */
const REDIRECT_ATTEMPTED_KEY = 'e6-handoff-redirect-attempted';

/**
 * True once this tab has already used its one deep-linked attempt.
 *
 * Every sessionStorage call in this module is wrapped in try/catch: it
 * throws outright in Safari private mode and when a browser is configured to
 * block site data, and this code path must never be the thing that breaks
 * sign-in. A failure to read degrades to "not attempted yet" — the same
 * direction markRedirectAttempted degrades in below, so a browser that can't
 * persist the marker at all just keeps deep-linking, which is no worse than
 * before this guard existed.
 */
function hasAttemptedRedirect(): boolean {
  try {
    return window.sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY) === '1';
  } catch {
    return false;
  }
}

function markRedirectAttempted(): void {
  try {
    window.sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, '1');
  } catch {
    // Best-effort — see hasAttemptedRedirect.
  }
}

/**
 * Clears the one-shot marker above once a session is confirmed live in this
 * tab, so a later, unrelated sign-out in the same tab still gets the deep
 * link instead of being stuck on the bare control-plane fallback for the
 * rest of the tab's life.
 *
 * Called from AuthProvider after a successful, authenticated /me response —
 * the one signal common to every way a session can become live in this tab.
 * The redirect-mode handoff (what UnauthenticatedScreen uses, and the flow
 * the loop guard above exists for) always reloads the SPA on return, which
 * re-runs /me; the popup-mode handoff in reauth.ts never calls
 * redirectToLogin at all when it succeeds, so there's nothing to clear on
 * that path. That asymmetry is also why this isn't hooked into
 * HandoffCompletePage's 'ok' report instead: that page runs inside the
 * popup's own browsing context, which per the HTML spec gets only a one-time
 * copy of the opener's sessionStorage taken when the popup opened — a write
 * there never reaches the opener tab's storage, so it couldn't clear the
 * marker the opener tab will actually consult next.
 */
export function clearRedirectAttempted(): void {
  try {
    window.sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
  } catch {
    // Best-effort — see hasAttemptedRedirect.
  }
}

/**
 * Sends the user to the control plane to sign in, and back to the page they
 * were on afterwards.
 *
 * The destination travels via the control plane's /workspace-handoff page, which
 * signs the user in if needed and then hands them back into this workspace at
 * the same path. When the workspace id is unknown — a workspace whose first poll
 * has not completed — this degrades to the bare control-plane URL, the behaviour
 * that shipped before deep-linked handoff existed.
 *
 * The deep link is a one-shot per tab. If a workspace session still can't be
 * established after bouncing through the control plane once — it stays
 * authenticated there but can't mint a working workspace session (control
 * plane unreachable from compute-api, a CP 5xx, a signing-key mismatch after
 * key rotation) — the control plane would otherwise mint a fresh one-time
 * code and send the user straight back, forever, once per page load. A
 * repeat call in the same tab instead falls back to the bare control-plane
 * URL, terminating exactly where sign-in landed before deep-linked handoff
 * existed. Only the deep-link path is guarded this way — the pre-existing
 * fallback used when the workspace id is unknown is untouched.
 *
 * Returns false (and does nothing) if no control-plane URL is configured, so
 * callers can fall back to a static "couldn't sign you in" message.
 */
export function redirectToLogin(reason: RedirectReason): boolean {
  const fallbackUrl = API_CONFIG.controlPlaneUrl;
  if (!fallbackUrl) {
    console.warn(
      `[auth] redirectToLogin(${reason}) skipped: no controlPlaneUrl. ` +
        'Check AGENT_API_BASE_URL on compute-api or VITE_CONTROL_PLANE_URL in dev.'
    );
    return false;
  }

  const deepLinkUrl = buildHandoffEntryUrl('redirect');
  let url: string;
  if (deepLinkUrl === null) {
    // Pre-existing degrade: workspace id unknown. No marker involved.
    url = fallbackUrl;
  } else if (hasAttemptedRedirect()) {
    // Loop guard: this tab already spent its one deep-linked attempt without
    // a session being established. Terminate where sign-in always used to —
    // the bare control-plane URL — instead of minting another one-time code
    // and bouncing forever.
    url = fallbackUrl;
  } else {
    url = deepLinkUrl;
    markRedirectAttempted();
  }

  console.info(`[auth] redirectToLogin(${reason}) → ${url}`);
  window.location.href = url;
  return true;
}
